import React, { useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Round 9 · F4 — shared duplicate-group resolve control.
 *
 * Owner mandate (Round 10): wire the resolve endpoints already on
 * the server for both Inventory count and Completeness so the yellow
 * dup banner can actually be cleared from the UI.
 *
 * Behaviour per group:
 *   - Pending (``resolution === ""``): auditor picks "Keep both" or
 *     "Exclude row + mandatory reason".
 *   - "Keep both": one click, POST goes through with no reason.
 *   - "Exclude row": pick which occurrence to exclude + type a
 *     non-empty reason. Button stays disabled until both are set.
 *   - Resolved (``resolution !== ""``): shows the resolution + who
 *     resolved it; no re-resolve from the UI (R6/R7 audit trail).
 *
 * ``entity`` is the server route segment: ``inventory-count`` or
 * ``completeness``. The resolve POST path is
 * ``/engagements/{eng}/{entity}/ref/{ref}/duplicates/{key}/resolve``.
 */
export default function DupGroupResolve({ group, entity, engId, ref_, resolveUrl, onResolved }) {
  const { t } = useI18n();
  const [choice, setChoice] = useState("");             // ""|"keep_both"|"exclude_one"
  const [excludeId, setExcludeId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const occurrences = group?.occurrences || [];
  const alreadyResolved = !!group?.resolution;
  const encodedKey = encodeURIComponent(group.key);
  const tid = `dup-${entity || "mfa"}-${group.key.replace(/[^A-Za-z0-9_-]/g, "_")}`;
  // Round 11 — allow a full URL override so the Multi-file Phase-2
  // panel can POST to /multi-file-attachments/… without changing the
  // ``/engagements/{eng}/{entity}/ref/{ref}/duplicates/…`` fallback.
  const url = resolveUrl
    || `/engagements/${engId}/${entity}/ref/${ref_}/duplicates/${encodedKey}/resolve`;

  const canSave =
    choice === "keep_both" ||
    (choice === "exclude_one" && !!excludeId && reason.trim().length > 0);

  const save = async () => {
    if (!canSave || busy) return;
    setBusy(true);
    try {
      await api.post(
        url,
        {
          resolution: choice,
          exclude_row_id: choice === "exclude_one" ? excludeId : "",
          reason: choice === "exclude_one" ? reason.trim()
                   : (reason.trim() || "Keep both — intentional"),
        });
      toast.success(t("dupResolve.savedToast") || "Duplicate flag resolved");
      onResolved?.();
    } catch (e) {
      toast.error(toErrMsg(e, "Resolve failed"));
    } finally { setBusy(false); }
  };

  if (alreadyResolved) {
    return (
      <div className="rounded border bg-slate-50 p-2 text-[11px]"
           data-testid={`${tid}-resolved`}>
        <div className="font-medium">
          {t("dupResolve.resolvedBy")
            ? t("dupResolve.resolvedBy").replace("{by}", group.resolved_by || "—")
            : `Resolved · ${group.resolved_by || "—"}`}
          {" · "}
          <span className="rounded bg-emerald-100 px-1 py-0.5 text-[10px] text-emerald-800">
            {group.resolution === "keep_both"
              ? (t("dupResolve.keepBoth") || "Keep both")
              : (t("dupResolve.excludeOne") || "Exclude row")}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded border border-amber-200 bg-amber-50/40 p-2 text-[11px]"
         data-testid={`${tid}-pending`}>
      <div className="mb-1 font-medium">
        {t("dupResolve.pending") || "Resolve this duplicate group"} ·
        <span className="ms-1 font-mono text-[10px] text-muted-foreground">
          {group.key}
        </span>
      </div>
      <ul className="mb-1 divide-y rounded border bg-white dark:bg-slate-900">
        {occurrences.map((o) => (
          <li key={o.row_id} className="flex items-center gap-2 px-2 py-1 text-[11px]"
              data-testid={`${tid}-occ-${o.row_id}`}>
            {choice === "exclude_one" && (
              <input type="radio" name={`${tid}-exclude`} value={o.row_id}
                     checked={excludeId === o.row_id}
                     onChange={() => setExcludeId(o.row_id)}
                     data-testid={`${tid}-pick-${o.row_id}`} />
            )}
            <span className="min-w-0 flex-1 break-words">
              <span className="font-mono text-muted-foreground">{o.filename}</span>
              <span className="ms-2">
                qty <b dir="ltr">{o.qty ?? "—"}</b> ·
                value <b dir="ltr">{o.value ?? "—"}</b>
              </span>
            </span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={choice === "keep_both" ? "default" : "outline"}
                className="h-6 text-[11px]"
                data-testid={`${tid}-choice-keep`}
                onClick={() => { setChoice("keep_both"); setExcludeId(""); }}>
          {t("dupResolve.keepBoth") || "Keep both"}
        </Button>
        <Button size="sm" variant={choice === "exclude_one" ? "default" : "outline"}
                className="h-6 text-[11px]"
                data-testid={`${tid}-choice-exclude`}
                onClick={() => setChoice("exclude_one")}>
          {t("dupResolve.excludeOne") || "Exclude row"}
        </Button>
        {choice === "exclude_one" && (
          <input className="h-7 flex-1 min-w-[180px] rounded border px-2 text-[11px]"
                 placeholder={t("dupResolve.reasonPh")
                   || "Reason (required) · السبب (مطلوب)"}
                 value={reason}
                 onChange={(e) => setReason(e.target.value)}
                 data-testid={`${tid}-reason`} />
        )}
        <Button size="sm" className="h-6 text-[11px]"
                disabled={!canSave || busy}
                onClick={save}
                data-testid={`${tid}-save`}>
          {busy ? (t("common.saving") || "Saving…")
                : (t("common.save") || "Save")}
        </Button>
      </div>
    </div>
  );
}
